// Pure helpers for building and merging the per-parameter Summary Statistics
// table that is snapshotted onto an assessment record. Extracted from
// src/pages/chart.js so the logic can be unit tested in isolation.

// Anderson-Darling rows. Both statistics are shown, but the Normality Assessment
// (ad_eval_sd) is evaluated ONLY from the RMS-sigma statistic (ad_rms_sd; sheet
// fullKeyParams!A46 tests A44). The MR-sigma statistic is informational.
export const AD_RMS_LABEL = 'Anderson Darling (RMS)';
export const AD_MR_LABEL = 'Anderson Darling (MR, informational)';

// Assessments saved before both rows existed stored ad_mr_sd under the bare
// "Anderson Darling" label (every pre-#518 build did), so that row IS the MR
// statistic. Relabel it so old records read correctly and re-saves merge into
// the MR row instead of leaving a stale duplicate.
const LEGACY_STATISTIC_LABELS = { 'Anderson Darling': AD_MR_LABEL };

export const relabelLegacyStatistics = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const label = LEGACY_STATISTIC_LABELS[row.statistic];
    return label ? { ...row, statistic: label } : row;
  });
};

// Compact "RMS / MR" cell for the pooled summary preview. RMS alone when the
// MR value is unavailable; empty when RMS is.
export const formatAdPair = (rms, mr, formatSig = (v) => v) => {
  const rmsText = formatSig(rms);
  if (rmsText == null || rmsText === '') return '';
  const mrText = formatSig(mr);
  return mrText == null || mrText === '' ? `${rmsText}` : `${rmsText} / ${mrText}`;
};

// Resolve the parameter a summary document describes. Prefer the explicit
// `parameter` field, but fall back to the parameter encoded in the document
// key (the last `|`-separated segment of the fullKey / doc id) because
// imported/legacy summary docs carry no `parameter` field.
export const resolveSummaryParameter = (summary) => {
  if (!summary) return undefined;
  if (summary.parameter != null && summary.parameter !== '') return summary.parameter;
  const key = summary.fullKey || summary.id;
  if (typeof key === 'string' && key.includes('|')) {
    // A trailing-pipe / empty last segment yields '', which is no more usable
    // as a column key than undefined — normalize it away.
    return key.split('|').pop() || undefined;
  }
  return undefined;
};

// Build the Summary Statistics rows for a set of summary docs. Each row is a
// statistic; each summary contributes one column keyed by its parameter.
export const buildStatisticsDataset = (summaries = [], config = [], formatters = {}) => {
  const {
    formatSig = (v) => v,
    formatInt = (v) => (v != null ? Math.round(v) : ''),
    formatRaw = (v) => v ?? ''
  } = formatters;

  const rows = config.map((item) => ({
    statistic: item.label
  }));

  summaries.forEach((summary) => {
    const param = resolveSummaryParameter(summary);
    // Without a resolvable parameter we cannot key this summary's column;
    // skip it rather than writing every value under a bogus key ("undefined"
    // or an empty string from a trailing-pipe/empty-segment key).
    if (param == null || param === '') return;

    config.forEach((item, index) => {
      const rawValue = summary[item.field];

      let value;
      switch (item.type) {
        case 'sig':
          value = formatSig(rawValue);
          break;
        case 'int':
          value = formatInt(rawValue);
          break;
        case 'raw':
        default:
          value = formatRaw(rawValue);
      }

      rows[index][param] = value;
    });
  });

  return rows;
};

// Merge an incoming per-parameter statistics dataset into any existing
// summaryStatistics rows, producing `{ statistic, values: { [param]: value } }`.
export const mergeSummaryStatistics = (existing = [], incoming = [], param) => {
  const map = new Map();

  // normalize existing. Legacy rows store their per-parameter values flat on
  // the row itself (no `.values` wrapper); lift those into `values` without
  // carrying the `statistic` key across as a value.
  relabelLegacyStatistics(existing).forEach((row) => {
    if (!row) return;
    let values;
    if (row.values) {
      values = { ...row.values };
    } else {
      const { statistic, ...rest } = row;
      values = rest;
    }

    // Fold rows that share a label (e.g. a relabelled legacy row) together.
    const prior = map.get(row.statistic);
    map.set(row.statistic, {
      statistic: row.statistic,
      values: prior ? { ...prior.values, ...values } : values
    });
  });

  // merge incoming. Never store `undefined` — Firestore rejects it anywhere in
  // the payload (this is what surfaced as "Failed to save assessment").
  incoming.forEach((row) => {
    const stat = row.statistic;
    const value = row[param];

    if (!map.has(stat)) {
      map.set(stat, { statistic: stat, values: {} });
    }

    if (value !== undefined) {
      map.get(stat).values[param] = value;
    }
  });

  return Array.from(map.values());
};
